/* eslint-disable camelcase */
import qs from 'qs'
import { isNil } from 'ramda'

import type {
  ArticleListResponse,
  ArticleResponse,
  BlocksAutoRatesConfigProductComponentProduct,
  BlogCategory,
  BlogPostResponse,
  BlogPostsResponseCustom,
  CategoryListResponse,
  CategoryListResponseDataItemCustom,
  CategoryResponse,
  GlobalResponse,
  NavigationResponse,
  PageListResponse,
  PageResponse,
  SearchArticlesResponse,
  SectionsEshopsComponent,
  StaticTextsConfigResponse,
} from '@twisto/api-cms'
import type {
  BenefitsData,
  EshopCategoryApiField,
  EshopsData,
} from '@twisto/api-cms/custom-types'
import { envConfig } from '@twisto/base/env-config'
import { fetchInstallmentsConfig } from '@twisto/base/fetch-installments-config'
import { remoteBackend } from '@twisto/environment'

import type { SectionType } from '../components'

import type { AutoRatesData } from './auto-rates/paths'
import { getConfigURLPath, getExampleURLSegment } from './auto-rates/paths'
import { readInstallmentsCalculatorConfig } from './installments-config.helpers'
import {
  calculatorConfigCZ,
  calculatorConfigPL,
} from './mocks/calculator-config'
import installmentsConfigCZ from './mocks/installments-config-cz'
import installmentsConfigPL from './mocks/installments-config-pl'

export type EshopCategory = NonNullable<SectionsEshopsComponent['category']>

export const CATEGORY_TO_INCLUDE_PARAM: Record<
  EshopCategory,
  EshopCategoryApiField
> = {
  popular: 'popular_eshops',
  newest: 'newest_eshops',
  'pay-in-3': 'pay_in_three_eshops',
  'pay-in-30': 'merchant_benefits',
  all: 'all',
}

export const ADDITIONAL_DATA_ENPOINTS = {
  benefits: 'benefits',
  eshops: {
    popular: 'eshops/popular',
    list: 'eshops/list',
  },
}

type StandardPageResponse = PageResponse & {
  data?: {
    title: string
    subtitle: string
    isRoot?: boolean
  }
  attributes: { contentSections?: SectionType[] }
}

type RootPageResponse = PageResponse & {
  title: string
  subtitle: string
  isRoot?: boolean
  attributes: { contentSections?: SectionType[] }
}

export const getStrapiURL = (path: string) =>
  `${process.env.STRAPI_API_URL ?? 'http://localhost:1337/api'}${path}`

export const getQueryString = (params: Record<string, unknown>): string => {
  const query = qs.stringify(params, {
    encodeValuesOnly: true,
  })

  return query.length > 0 ? `?${query}` : ''
}

const fetchAPI = async (
  requestUrl: string,
  urlParamsObject = {},
  options = {}
) => {
  const mergedOptions = {
    headers: {
      'Content-Type': 'application/json',
    },
    ...options,
  }

  const queryString = getQueryString(urlParamsObject)
  try {
    const response = await fetch(`${requestUrl}${queryString}`, mergedOptions)
    const data = await response.json()

    return data
  } catch (error) {
    return {}
  }
}

const fetchStrapiAPI = async (
  path: string,
  urlParamsObject = {},
  options = {}
) => {
  const requestUrl = getStrapiURL(path)

  return await fetchAPI(requestUrl, urlParamsObject, options)
}

export const getPages = async (): Promise<PageListResponse> =>
  await fetchStrapiAPI('/pages', { fields: 'path' })

export const getPage = async (url: string): Promise<StandardPageResponse> =>
  await fetchStrapiAPI(`/page/${encodeURIComponent(url)}`)

export const getConditionsPage = async (
  url: string
): Promise<StandardPageResponse> =>
  await fetchStrapiAPI(`/conditions-page/${encodeURIComponent(url)}`)

export const getConditionsRootPage = async (): Promise<RootPageResponse> =>
  await fetchStrapiAPI(`/conditions-page/root`)

export const getConditionsSection = async (): Promise<PageResponse> =>
  await fetchStrapiAPI(`/conditions-sections`, {
    populate: {
      conditions_pages: true,
      conditions_folders: {
        populate: ['conditions_pages'],
      },
    },
  })

export const getNavigation = async (
  id: string | number
): Promise<NavigationResponse> => await fetchStrapiAPI(`/navigations/${id}`)

export const getMediaArticle = async (path: string) =>
  await fetchStrapiAPI(`/media-article/${encodeURIComponent(path)}`)

export const getMediaArticles = async () =>
  await fetchStrapiAPI('/media-articles')

export const getGlobalMetadata = async (): Promise<GlobalResponse> =>
  await fetchStrapiAPI('/global')

export const getCategories = async (): Promise<CategoryListResponse> =>
  await fetchStrapiAPI('/categories', { fields: 'path' })

export const getCategory = async (
  url: string
): Promise<
  CategoryResponse & {
    data: CategoryListResponseDataItemCustom
  }
> => await fetchStrapiAPI(`/categories/${encodeURIComponent(url)}`)

export const getArticle = async (url: string): Promise<ArticleResponse> =>
  await fetchStrapiAPI(`/articles/${encodeURIComponent(url)}`)

export const getArticles = async (): Promise<ArticleListResponse> =>
  await fetchStrapiAPI('/articles', { fields: 'path' })

export const getStaticTexts = async (): Promise<StaticTextsConfigResponse> =>
  await fetchStrapiAPI('/static-texts-config')

export const getPopularArticles = async (
  rootCategoryId?: string | number
): Promise<ArticleListResponse> =>
  await fetchStrapiAPI('/articles', {
    'filters[isPopular]': true,
    'pagination[pageSize]': 15,
    ...(!isNil(rootCategoryId) && {
      'filters[rootCategoryId]': rootCategoryId,
    }),
  })

export const searchArticles = async (
  searchTerm: string,
  pageSize: number,
  rootCategoryId?: number
): Promise<SearchArticlesResponse> =>
  await fetchAPI('/api/article-search', {
    query: encodeURIComponent(searchTerm),
    'pagination[articles][pageSize]': pageSize,
    ...(!isNil(rootCategoryId) && {
      'filters[articles][rootCategoryId]': rootCategoryId,
    }),
    'populate[articles][category][populate][parent][fields][0]': '*',
  })

export const getBlogPost = async (url: string): Promise<BlogPostResponse> =>
  await fetchStrapiAPI(`/blog_post/${encodeURIComponent(url)}`)

export const getBlogPosts = async (
  page: number,
  pageSize: number,
  categoryId?: string | number,
  newest?: boolean
): Promise<BlogPostsResponseCustom> =>
  await fetchAPI('/api/blog-posts', {
    page,
    pageSize,
    categoryId,
    newest,
  })

export const getBlogCategories = async (): Promise<BlogCategory[] | null> =>
  await fetchAPI('/api/blog-categories')

export const getAdditionalData = async <T>(
  endpoint: string
): Promise<T | null> => {
  // Deployed Homepage data stays pinned to its CMS configuration, not Host.
  const baseUrl =
    remoteBackend?.apiProxyOrigin ??
    (process.env.CMS_API_URL ?? '')
      .replace(/https?:\/\/cms\./, 'https://api.')
      .replace(/\/api$/, '')
  const url = `${baseUrl}/${endpoint}`

  return await fetchAPI(url)
}

export const getEshops = async (
  endpoint: keyof typeof ADDITIONAL_DATA_ENPOINTS.eshops,
  category?: string
): Promise<EshopsData | null> => {
  const shouldIncludeCategory = category && category !== 'all'
  const requestUrl = `${ADDITIONAL_DATA_ENPOINTS.eshops[endpoint]}${shouldIncludeCategory ? `?include=${category}` : ''}`

  if (envConfig.MOCK_API) {
    return {
      eshops: [],
      eshop_categories: [],
      popular_eshops: [],
      newest_eshops: [],
      pay_in_three_eshops: [],
    }
  }

  if (!requestUrl) {
    return null
  }

  return await getAdditionalData<EshopsData>(requestUrl).then((data) => {
    if (!data) {
      return null
    }

    return data
  })
}

export const getBenefits = async (): Promise<BenefitsData | null> => {
  const requestUrl = ADDITIONAL_DATA_ENPOINTS.benefits

  return await getAdditionalData<BenefitsData>(requestUrl).then((data) => {
    if (!data) {
      return null
    }

    return data
  })
}

export const validateInvoiceId = async (
  invoiceId: string,
  email: string
): Promise<{ valid: boolean } | null> => {
  const apiOrigin = window.location.origin.replace('//www.', '//api.')

  const response = await fetch(`${apiOrigin}/invoice/validate/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      invoice_id: invoiceId,
      email,
    }),
  })

  if (!response.ok) {
    return null
  }

  const data = await response.json()

  return data
}

/** Calculators Config Api
 * - deferral
 * - instalments
 */

export const fetchDeferredCalcConfig = async (
  baseUrl: string,
  region: 'CZ' | 'PL'
) => {
  const previousConfig =
    region === 'PL' ? calculatorConfigPL : calculatorConfigCZ

  if (envConfig.MOCK_API) {
    return previousConfig
  }

  try {
    const response = await fetch(`${baseUrl}/deferred-payment/config`, {
      headers: {
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      return previousConfig
    }

    return await response.json()
  } catch {
    return previousConfig
  }
}

export const fetchInstallmentsCalcConfig = async (
  baseUrl: string,
  region?: 'CZ' | 'PL'
) => {
  const resolvedRegion = region ?? (baseUrl.includes('pl') ? 'PL' : 'CZ')
  const previousConfig =
    resolvedRegion === 'PL' ? installmentsConfigPL : installmentsConfigCZ

  if (envConfig.MOCK_API) {
    return previousConfig
  }

  // Config endpoints are not expected to fail. The fallback is added for calculators UI to work during migration
  try {
    return readInstallmentsCalculatorConfig(
      await fetchInstallmentsConfig(
        baseUrl,
        { headers: { 'Content-Type': 'application/json' } },
        false
      )
    )
  } catch {
    return previousConfig
  }
}

export const fetchAutoRatesConfig = async (
  baseUrl: string,
  product: BlocksAutoRatesConfigProductComponentProduct
): Promise<AutoRatesData | undefined> => {
  const path = getConfigURLPath(product)
  if (!path) {
    return undefined
  }

  const url = `${baseUrl}/${path}`

  try {
    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
      },
    })

    return { [product]: await response.json() }
  } catch (err) {
    return {}
  }
}

export const fetchAutoRatesExample = async (
  baseUrl: string,
  product: BlocksAutoRatesConfigProductComponentProduct,
  amount: string
): Promise<AutoRatesData | undefined> => {
  const segment = getExampleURLSegment(product)
  if (!segment) {
    return undefined
  }

  const url = `${baseUrl}/${segment}/config/example/?amount=${amount}`

  try {
    const response = await fetch(url, {
      headers: { 'Content-Type': 'application/json' },
    })

    const bodyText = await response.text()
    if (!response.ok) {
      return undefined
    }

    return { [product]: JSON.parse(bodyText) }
  } catch {
    return {}
  }
}
